import React, { useEffect, useState } from 'react';
import { makeStyles } from 'design-system/styles';
import { Button, H1, H2 } from 'design-system';
import { TextInput } from 'react-scale/components/design-system';
import SanitizedMarkdown from 'design-system/SanitizedMarkdown';
import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import Select from 'corp/components/UI/Select';
import { Section } from 'react-scale/containers/corp/backfills/task/components/Section';

const CHERRY_INF_PROJECT_ID = '611418bc5f47eb002665fba7';
const CHERRY_INF_USER_ID = '6165be39e7bd52002697ffe1';

const CherryINFPage: React.FC = () => {
  const styles = useStyles();

  return (
    <div className={styles.container}>
      <H1>Cherry INF Utils</H1>
      <Section name={`Download completed tasks in a Batch`} forceHidden={true}>
        <SelectBatch />
      </Section>
      <Section name={`Process files`} forceHidden={true}>
        <ProcessFiles />
      </Section>
      <style jsx>{`
        .root .two-columns {
          display: flex;
        }
        .root .two-columns section {
          flex-basis: 50%;
        }
        .root .two-columns section.disabled {
          color: rgba(0, 0, 0, 0.3);
        }
        .root .three-columns {
          display: flex;
        }
        .root .three-columns section {
          flex-basis: 33%;
        }
        .root .three-columns section.disabled {
          color: rgba(0, 0, 0, 0.3);
        }
        .root .disallowed-task-types {
          font-weight: bold;
          color: red;
        }
        .root .warning {
          font-weight: bold;
          color: red;
        }
        .root.disabled section {
          color: rgba(0, 0, 0, 0.3);
        }
      `}</style>
    </div>
  );
};

const ProcessFiles = () => {
  const [isLoading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string | null>(null);
  const [files, setFiles] = useState<string[]>([]);

  const onInput = (e: any) => {
    setFiles([
      ...new Set(
        (e.target.value as string)
          .split(`\n`)
          .map((x: string) => x.trim())
          .filter((x: string) => x !== '')
          .map((x: string) => `inf-batches-3f1d18/${x}`),
      ),
    ]);
  };

  const processFiles = async () => {
    setLoading(true);
    setMessage(null);
    try {
      const { message }: { message: string } = await apiFetch(
        null,
        'corp-api/cherry-inf-utils/process-files',
        {
          method: 'POST',
          body: { files },
        },
      );
      setMessage(message);
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
    }
    setLoading(false);
  };

  return (
    <div className={'root'}>
      <section>
        <TextInput
          type="text"
          defaultValue={''}
          placeholder="Line-separated file names"
          maxRows={10}
          multiline={true}
          fullWidth={true}
          disabled={isLoading}
          onChange={onInput}
        />
      </section>
      {files.length > 0 && (
        <ol>
          {files.map(file => (
            <li key={file}>{file}</li>
          ))}
        </ol>
      )}
      <br />
      <Button onClick={processFiles} disabled={isLoading || files.length === 0}>
        {isLoading ? 'Processing' : `Process ${files.length} Files`}
      </Button>
      {message && <div>{<SanitizedMarkdown text={message} />}</div>}
    </div>
  );
};

const SelectBatch = () => {
  const [isLoading, setLoading] = useState<boolean>(false);
  const [batchOptions, setBatchOptions] = useState<any[]>([]);
  const [batchSelected, setBatchSelected] = useState<any>(null);
  const [batchCount, setBatchCount] = useState<number>(-1);

  const handleBatchSearch = async () => {
    apiFetch(null, 'corp-api/task-actions-lite/project-batches', {
      method: 'POST',
      body: { project: CHERRY_INF_PROJECT_ID },
    })
      .then(result => {
        setBatchOptions(
          result.batches.map((batch: Record<string, string>) => {
            return { label: batch.name, value: batch._id };
          }),
        );
        setLoading(false);
        return;
      })
      .catch(error => {
        defaultApiFetchErrorHandler(error, { handleHttp: true });
        setLoading(false);
      });
  };

  useEffect(() => {
    handleBatchSearch();
  }, []);

  const downloadBatch = () => {
    setLoading(true);
    apiFetch(null, `corp-api/cherry-inf-utils/download-batch`, {
      method: 'POST',
      body: {
        project: CHERRY_INF_PROJECT_ID,
        user: CHERRY_INF_USER_ID,
        batchName: batchSelected.label,
      },
    })
      .then(result => {
        const blob = new Blob(result.csvData);
        const a = window.document.createElement('a');

        a.href = window.URL.createObjectURL(blob);
        a.download = result.fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setBatchCount(-1);
        setBatchSelected(null);
        setLoading(false);
        return;
      })
      .catch(error => {
        defaultApiFetchErrorHandler(error, { handleHttp: true });
        setLoading(false);
      });
  };

  const handleChange = (batch: $FIXME) => {
    setBatchSelected(batch);
    setBatchCount(-1);
    if (batch !== null) {
      const url = `corp-api/cherry-inf-utils/batch-count?user=${CHERRY_INF_USER_ID}&project=${CHERRY_INF_PROJECT_ID}&batchName=${batch.label}`;
      apiFetch(null, url, { method: 'GET' })
        .then(result => {
          setBatchCount(result.batchCount);
          return;
        })
        .catch(defaultApiFetchErrorHandler);
    }
  };

  return (
    <div className={'root'}>
      <section>
        <H2>Batch Download</H2>
        <p>Downloads all completed tasks from the selected batch.</p>
        <Select
          placeholder="Select Batch..."
          value={batchSelected}
          disabled={isLoading}
          onChange={(value: $FIXME) => handleChange(value)}
          options={batchOptions}
          getOptionLabel={(option: Record<string, string>) => option.name}
          getOptionValue={(option: Record<string, string>) => option._id}
        />
      </section>
      <br />
      {batchCount >= 0 && (
        <li>
          {batchCount} completed tasks to download from Batch {batchSelected.label}
        </li>
      )}
      <br />
      <Button onClick={downloadBatch} disabled={isLoading}>
        {isLoading ? `Downloading...` : 'Download CSV'}
      </Button>
    </div>
  );
};

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
});

export default CherryINFPage;
